export class Color {
  rgba: number[];
  hex: string;
  hsl: number[];
  channels: string;
  isValid: boolean;
  constructor(color: string) {
    this.setup(color);
  }
  changeColor(color: string): void {
    this.setup(color);
  }
  setup(color: string) {
    if (!color) {
      console.error('Color is invalid or not provided', color);
      this.isValid = false;
      return;
    }
    this.isValid = true;
    if (color.includes('rgb')) {
      this.rgba = color
        .replace('rgba(', '')
        .replace('rgb(', '')
        .replace(')', '')
        .split(',')
        .map(channel => parseInt(channel));
      if (this.rgba.length === 3) {
        this.rgba.push(1);
      }
      this.hex = this.rgbaToHex(color);
      this.hsl = this.rgbaToHsl(this.rgba);
      this.channels = this.hexToChannel(this.hex);
      return;
    }
    if (color.includes('hsl')) {
      this.hsl = color
        .replace('hsl(', '')
        .replace(')', '')
        .replaceAll('%', '')
        .replace('deg', '')
        .split(',')
        .map(channel => parseInt(channel));
      this.hex = this.hslToHex(this.hsl);
      this.rgba = this.hexToRgba(this.hex);
      this.channels = this.hexToChannel(this.hex);
      return;
    }
    if (color.includes('#')) {
      this.hex = color;
      this.rgba = this.hexToRgba(color);
      this.hsl = this.rgbaToHsl(this.rgba);
      this.channels = this.hexToChannel(color);
      return;
    }
  }

  private hexToRgba(hex: string): number[] {
    let sanitizedHex = hex.replace(/^#/, '');
    if (sanitizedHex.length === 3) {
      sanitizedHex = sanitizedHex + sanitizedHex; // to convert 3 characters hexcode to 6 characters hexcode (eg:  #fff -> #ffffff)
    }
    const channels = sanitizedHex.match(/.{1,2}/g)!;
    const rgba: number[] = channels.map(channel => parseInt(channel, 16));

    if (rgba.length === 3) {
      rgba.push(1);
    }

    if (rgba.includes(NaN)) {
      this.isValid = false;
    }

    return rgba;
  }

  private rgbaToHsl(rgba: number[]): number[] {
    const [r, g, b] = rgba.map(channel => channel / 255);

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }

      h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  }

  private hslToHex(hsl: number[]): string {
    const [h, s, l] = hsl;
    const hueToRgb = (p: number, q: number, t: number) => {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 2) {
        return q;
      }
      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }
      return p;
    };

    const hue = h / 360;
    const saturation = s / 100;
    const lightness = l / 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = lightness;
    } else {
      const q = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation;
      const p = 2 * lightness - q;
      r = hueToRgb(p, q, hue + 1 / 3);
      g = hueToRgb(p, q, hue);
      b = hueToRgb(p, q, hue - 1 / 3);
    }

    return `#${Math.round(r * 255)
      .toString(16)
      .padStart(2, '0')}${Math.round(g * 255)
      .toString(16)
      .padStart(2, '0')}${Math.round(b * 255)
      .toString(16)
      .padStart(2, '0')}`;
  }

  private hexToChannel(hex: string): string {
    hex = hex.replace('#', '');

    if (hex.length !== 3 && hex.length !== 6) {
      console.error('Invalid hex color length', hex);
      this.isValid = false;
      return '';
    }

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('');
    }

    const red = parseInt(hex.substring(0, 2), 16);
    const green = parseInt(hex.substring(2, 4), 16);
    const blue = parseInt(hex.substring(4, 6), 16);

    if ([red, green, blue].includes(NaN)) {
      this.isValid = false;
    }

    return `${red} ${green} ${blue}`;
  }

  private rgbaToHex(rgba: string, withAlpha = false): string {
    if (!rgba) {
      return '';
    }

    const match = rgba.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*(\d*(?:\.\d+)?(?:\.\d+)?)\)$/);
    if (!match) {
      console.error('Invalid rgba color', rgba);
      this.isValid = false;
      return rgba;
    }

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = Math.round(parseFloat(match[4]) * 255);

    const hexR = r.toString(16).padStart(2, '0');
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');
    const hexA = a.toString(16).padStart(2, '0');

    if (withAlpha) {
      return `#${hexR}${hexG}${hexB}${hexA}`;
    }

    return `#${hexR}${hexG}${hexB}`;
  }

  getColorObject = () => {
    return {
      red: this.rgba[0],
      green: this.rgba[1],
      blue: this.rgba[2],
      alpha: this.rgba[3] || 1,
      style: `rgba(${this.rgba.join(',')})`
    };
  };
}
